import React, { useEffect, useRef, useState } from "react";
import { logWarn } from "../lib/logger";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Save, Coffee, ArrowUpRight, HelpCircle, AlertTriangle, Trash2, Download, Bookmark } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";

const CONFIRM_PHRASE = "delete my account";

const defaults = {
  show_rejected_in_main: false,
  auto_archive_on_rejection: true,
  reminders_inapp: true,
  nudge_days: 14,
  default_visibility: "link",
};

export default function Settings() {
  const { deleteAccount } = useAuth();
  const { refresh: refreshSettings } = useSettings();
  const [s, setS] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const [engagement, setEngagement] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const bookmarkletRef = useRef(null);

  useEffect(() => {
    if (!bookmarkletRef.current) return;
    const origin = window.location.origin;
    const code = `javascript:(function(){window.open('${origin}/jobs/new?url='+encodeURIComponent(window.location.href),'_blank');})();`;
    bookmarkletRef.current.setAttribute("href", code);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [r, e] = await Promise.all([api.get("/settings"), api.get("/engagement")]);
        setS({ ...defaults, ...r.data });
        setShowSupport(!!e.data.show_support);
        setEngagement(e.data);
      } catch (error) {
        logWarn("Failed to load settings:", error);
      }
    })();
  }, []);

  const setKey = (k) => (v) => setS({ ...s, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", s);
      await refreshSettings();
      toast.success("Settings saved");
    } catch (error) {
      logWarn("Save settings failed:", error);
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const r = await api.get("/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reinvocation-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Your data has been downloaded");
    } catch (error) {
      logWarn("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteAccount();
      window.location.replace("/goodbye");
    } catch (error) {
      logWarn("Delete account failed:", error);
      toast.error("Could not delete account. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto" data-testid="settings-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tiny">Account</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight mt-2">Settings</h1>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-10 px-5" data-testid="settings-save-btn">
          <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <Section title="Display">
        <Row
          tid="setting-show-rejected"
          label="Show rejected jobs in main view"
          desc="Display rejected jobs alongside active ones."
        >
          <Switch checked={s.show_rejected_in_main} onCheckedChange={setKey("show_rejected_in_main")} data-testid="toggle-show-rejected" />
        </Row>
        <Row
          tid="setting-auto-archive"
          label="Auto-archive upon rejection"
          desc="When on, rejected jobs are archived immediately without prompting."
        >
          <Switch checked={s.auto_archive_on_rejection} onCheckedChange={setKey("auto_archive_on_rejection")} data-testid="toggle-auto-archive" />
        </Row>
      </Section>

      <Section title="Application reminders">
        <Row tid="setting-reminders-inapp" label="In-app reminders" desc="Show me a notification when a job sits without movement.">
          <Switch checked={s.reminders_inapp} onCheckedChange={setKey("reminders_inapp")} data-testid="toggle-reminders-inapp" />
        </Row>
        <div className="brutal-card p-5">
          <Label className="label-tiny block mb-3">Remind me after no movement for</Label>
          <Select value={String(s.nudge_days)} onValueChange={(v) => setKey("nudge_days")(Number(v))}>
            <SelectTrigger className="rounded-none h-11 max-w-xs" data-testid="select-nudge-days"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7" data-testid="nudge-7">7 days</SelectItem>
              <SelectItem value="14" data-testid="nudge-14">14 days</SelectItem>
              <SelectItem value="30" data-testid="nudge-30">30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Section>

      <Section title="Default job visibility">
        <div className="brutal-card p-5">
          <Label className="label-tiny block mb-3">When you create a new job, default it to</Label>
          <Select value={s.default_visibility} onValueChange={setKey("default_visibility")}>
            <SelectTrigger className="rounded-none h-11 max-w-xs" data-testid="select-visibility"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="public" data-testid="visibility-public">Public</SelectItem>
              <SelectItem value="link" data-testid="visibility-link">Only those with a link</SelectItem>
              <SelectItem value="private" data-testid="visibility-private">Private</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Section>

      {showSupport && (
        <Section title="Support ReInVocation">
          <div className="relative">
            <a
              href="https://ko-fi.com/suddengenesis"
              target="_blank" rel="noopener noreferrer"
              data-testid="settings-kofi-link"
              className="brutal-card p-5 flex items-center justify-between gap-6 transition-colors hover:bg-zinc-50 group"
            >
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center shrink-0">
                  <Coffee size={18} strokeWidth={1.75} />
                </div>
                <div>
                  <div className="font-medium">Help fund the site</div>
                  <p className="text-sm text-zinc-600 mt-1">
                    ReInVocation is built and maintained by one person. If it's helping your job hunt, a coffee on Ko-fi keeps the lights on.
                  </p>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-zinc-400 group-hover:text-zinc-900 shrink-0" />
            </a>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    data-testid="kofi-why-btn"
                    className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-900 transition-colors"
                    aria-label="Why am I seeing this?"
                    onClick={(e) => e.preventDefault()}
                  >
                    <HelpCircle size={14} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="left" className="rounded-none max-w-[260px] text-xs leading-relaxed">
                  <div className="font-semibold mb-1">Why am I seeing this?</div>
                  {whyReasons(engagement).map((r) => (
                    <div key={r}>· {r}</div>
                  ))}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </Section>
      )}

      <Section title="Browser tools">
        <div className="brutal-card p-5" data-testid="bookmarklet-section">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center shrink-0">
              <Bookmark size={18} strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium">"Save this job" bookmarklet</div>
              <p className="text-sm text-zinc-600 mt-1">
                Drag the button below to your bookmarks bar. On any job listing, click it to instantly
                open ReInVocation with that page ready to auto-fill.
              </p>
              <a
                ref={bookmarkletRef}
                href="/"
                onClick={(e) => e.preventDefault()}
                draggable="true"
                data-testid="bookmarklet-link"
                className="inline-flex items-center gap-2 mt-4 bg-zinc-900 text-white px-4 h-10 cursor-grab active:cursor-grabbing select-none hover:bg-zinc-800 transition-colors"
              >
                <Bookmark size={14} /> Save to ReInVocation
              </a>
            </div>
          </div>
        </div>
      </Section>

      <Section title="Your data">
        <div className="brutal-card p-5 flex items-start justify-between gap-6" data-testid="export-data-section">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-zinc-900 text-white flex items-center justify-center shrink-0">
              <Download size={18} strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-medium">Export my data</div>
              <p className="text-sm text-zinc-600 mt-1">
                Download a JSON file of your master resume and all saved jobs. AI-generated cover
                letters and tailored resumes are not stored and are not included.
              </p>
            </div>
          </div>
          <Button
            onClick={handleExport}
            disabled={exporting}
            data-testid="export-data-btn"
            variant="outline"
            className="rounded-none h-10 px-5 border-zinc-300 shrink-0"
          >
            <Download size={14} className="mr-1.5" /> {exporting ? "Preparing…" : "Export"}
          </Button>
        </div>
      </Section>

      <Section title="Danger zone">
        <div className="border-2 border-red-600 bg-red-50 p-5 flex items-start justify-between gap-6" data-testid="danger-zone">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-red-600 text-white flex items-center justify-center shrink-0">
              <AlertTriangle size={18} strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-medium text-red-900">Delete my account</div>
              <p className="text-sm text-red-700 mt-1">
                Permanently delete your account and all associated data — jobs, resume, settings, and
                history. This action cannot be undone.
              </p>
            </div>
          </div>
          <Button
            onClick={() => { setConfirmText(""); setDeleteOpen(true); }}
            data-testid="open-delete-account-btn"
            className="rounded-none bg-red-600 hover:bg-red-700 text-white h-10 px-5 shrink-0"
          >
            <Trash2 size={14} className="mr-1.5" /> Delete
          </Button>
        </div>
      </Section>

      <Dialog open={deleteOpen} onOpenChange={(o) => !deleting && setDeleteOpen(o)}>
        <DialogContent className="rounded-none border-2 border-red-600" data-testid="delete-account-modal">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black tracking-tight text-red-900">
              Delete your account?
            </DialogTitle>
            <DialogDescription className="text-zinc-700 leading-relaxed">
              This will permanently erase everything: all saved jobs, your resume, settings, and
              engagement history. There is no recovery.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="label-tiny block">
              Type <span className="font-mono font-bold text-red-700">{CONFIRM_PHRASE}</span> to confirm
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={CONFIRM_PHRASE}
              autoFocus
              data-testid="delete-confirm-input"
              className="rounded-none h-11 border-zinc-300 focus-visible:ring-red-600"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
              disabled={deleting}
              data-testid="delete-cancel-btn"
              className="rounded-none h-11 px-5 border-zinc-300"
            >
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={confirmText.trim().toLowerCase() !== CONFIRM_PHRASE || deleting}
              data-testid="confirm-delete-account-btn"
              className="rounded-none h-11 px-5 bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
            >
              <Trash2 size={14} className="mr-1.5" /> {deleting ? "Deleting…" : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="flex justify-end py-8">
        <Button onClick={save} disabled={saving} className="rounded-none bg-zinc-900 hover:bg-zinc-800 text-white h-11 px-6" data-testid="settings-save-btn-2">
          <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : "Save settings"}
        </Button>
      </div>
    </div>
  );
}

const Section = ({ title, children }) => (
  <section className="mt-10">
    <h2 className="font-heading text-xl sm:text-2xl font-bold tracking-tight mb-4">{title}</h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const Row = ({ tid, label, desc, children }) => (
  <div className="brutal-card p-5 flex items-start justify-between gap-6" data-testid={tid}>
    <div>
      <div className="font-medium">{label}</div>
      <p className="text-sm text-zinc-600 mt-1">{desc}</p>
    </div>
    <div className="shrink-0">{children}</div>
  </div>
);

const whyReasons = (e) => {
  if (!e) return ["You've been actively using ReInVocation."];
  const out = [];
  if (e.job_count >= 5) out.push(`You've saved ${e.job_count} jobs.`);
  if (e.has_active_job) out.push("You've moved a job into your active pipeline (Applied / Interviewing / Offer).");
  if (e.ai_tailor_used) out.push("You used AI resume tailoring.");
  if (e.ai_cover_used) out.push("You generated an AI cover letter.");
  if (e.linkedin_imported) out.push("You imported your resume from LinkedIn.");
  return out.length ? out : ["You've been actively using ReInVocation."];
};
